import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Box, BoxProps } from '@chakra-ui/react';
import { Button, ButtonProps, Colors, Icon } from '@mercator-gcp/grout';
import { Z_INDEX_DICT } from 'utils/constants/zIndex';

// Shared sidebar building blocks. Re-exported here so consuming pages can pull
// the whole sidebar vocabulary from a single import.
export { SidebarTitle } from 'components/layout/SidebarLayout/SidebarTitle';
export { SidebarSectionLabel } from 'components/layout/SidebarLayout/SidebarSectionLabel';
export {
  SidebarFooter,
  SidebarClearAllFooter,
} from 'components/layout/SidebarLayout/SidebarFooter';

/**
 * Standardized two-column page layout: a fixed-width sidebar with a divider
 * line next to a flexible main content area. Shared across the filter
 * pages (Company Search, Project Insights) and detail pages (Company
 * Details, Account Settings).
 *
 * The sidebar's inner padding/overflow is page-specific — pass it via
 * `sidebarSx`. The main content container is rendered by the consuming page
 * as `children`, so it keeps full control of its own padding and scrolling.
 *
 * When `hideSidebarOnMobile` is set, below the mobile breakpoint the sidebar
 * is hidden and re-opens as an overlay drawer. The trigger is rendered by the
 * page via `<SidebarFilterToggle />` (so it can sit inline beside the page's
 * search input); it drives the drawer through context.
 */

// Divider line between the sidebar and the main content area (replaces the
// former drop shadow so the layout renders flat).
const SIDEBAR_BORDER = `1px solid ${Colors.ArmyGreen300}`;

// Below this breakpoint the sidebar is hidden on filter pages and replaced by
// the toggle button + overlay drawer.
const SIDEBAR_MOBILE_BREAKPOINT = 'lg';

type TSidebarLayoutContext = {
  isMobileSidebarOpen: boolean;
  toggleMobileSidebar: () => void;
};

const SidebarLayoutContext = createContext<TSidebarLayoutContext | null>(null);

const toggleButtonStyle = {
  display: { base: 'inline-flex', [SIDEBAR_MOBILE_BREAKPOINT]: 'none' },
  flexShrink: 0,
  height: '48px',
  minWidth: '48px',
  paddingX: 'padding_12',
  backgroundColor: 'white',
};

/**
 * Kebab button that toggles the mobile filter drawer. Renders only below the
 * mobile breakpoint and only inside a `SidebarLayout`. Drop it beside a page's
 * search input.
 */
export const SidebarFilterToggle = ({ sx }: { sx?: ButtonProps['sx'] }) => {
  const context = useContext(SidebarLayoutContext);

  if (!context) {
    return null;
  }

  return (
    <Button
      aria-label="Toggle filters"
      variant="outline"
      onClick={context.toggleMobileSidebar}
      sx={{ ...toggleButtonStyle, ...sx }}
    >
      <Icon name={Icon.Name.Filter} size={20} fill={Colors.ArmyGreen700} />
    </Button>
  );
};

type TSidebarLayoutProps = {
  /** Content rendered inside the sidebar column. */
  sidebar: ReactNode;
  /** Main content, rendered as the second grid column. */
  children: ReactNode;
  /** Sidebar column width. Accepts any valid grid track value. */
  sidebarWidth?: string;
  /** z-index applied to the sidebar column (see Z_INDEX_DICT). */
  sidebarZIndex?: string | number;
  /** Hide the sidebar below the mobile breakpoint (filter pages). */
  hideSidebarOnMobile?: boolean;
  /** Page-specific sidebar styling (padding, overflow, etc.). */
  sidebarSx?: BoxProps['sx'];
  /** Overrides for the grid container. */
  sx?: BoxProps['sx'];
};

export const SidebarLayout = ({
  sidebar,
  children,
  sidebarWidth = '240px',
  sidebarZIndex,
  hideSidebarOnMobile = false,
  sidebarSx,
  sx,
}: TSidebarLayoutProps) => {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const toggleMobileSidebar = useCallback(
    () => setIsMobileSidebarOpen((isOpen) => !isOpen),
    [],
  );

  const contextValue = useMemo(
    () => ({ isMobileSidebarOpen, toggleMobileSidebar }),
    [isMobileSidebarOpen, toggleMobileSidebar],
  );

  const twoColumns = `${sidebarWidth} minmax(0, 1fr)`;
  const gridTemplateColumns = hideSidebarOnMobile
    ? { base: 'minmax(0, 1fr)', [SIDEBAR_MOBILE_BREAKPOINT]: twoColumns }
    : twoColumns;

  // Base layout: a normal grid column. When `hideSidebarOnMobile`, below the
  // breakpoint the sidebar becomes an absolutely-positioned overlay drawer
  // toggled by `isMobileSidebarOpen`, and reverts to a static grid column at
  // the breakpoint and above.
  const sidebarStyle = hideSidebarOnMobile
    ? {
        height: '100%',
        borderRight: SIDEBAR_BORDER,
        background: 'white',
        ...sidebarSx,
        position: { base: 'absolute', [SIDEBAR_MOBILE_BREAKPOINT]: 'static' },
        top: 0,
        bottom: 0,
        left: 0,
        width: { base: sidebarWidth, [SIDEBAR_MOBILE_BREAKPOINT]: 'auto' },
        display: {
          base: isMobileSidebarOpen ? 'block' : 'none',
          [SIDEBAR_MOBILE_BREAKPOINT]: 'block',
        },
        zIndex: {
          base: Z_INDEX_DICT.MOBILE_FILTER_DRAWER,
          [SIDEBAR_MOBILE_BREAKPOINT]: sidebarZIndex,
        },
      }
    : {
        height: '100%',
        borderRight: SIDEBAR_BORDER,
        zIndex: sidebarZIndex,
        ...sidebarSx,
      };

  return (
    <SidebarLayoutContext.Provider value={contextValue}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns,
          transition: 'grid-template-columns 200ms ease',
          height: '100%',
          width: '100%',
          position: 'relative',
          ...sx,
        }}
      >
        <Box sx={sidebarStyle}>{sidebar}</Box>
        {children}
        {hideSidebarOnMobile && isMobileSidebarOpen && (
          <Box
            onClick={() => setIsMobileSidebarOpen(false)}
            sx={{
              display: { base: 'block', [SIDEBAR_MOBILE_BREAKPOINT]: 'none' },
              position: 'absolute',
              inset: 0,
              background: 'blackAlpha.400',
              zIndex: Z_INDEX_DICT.MOBILE_FILTER_BACKDROP,
            }}
          />
        )}
      </Box>
    </SidebarLayoutContext.Provider>
  );
};

export default SidebarLayout;
